import React, { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate, Navigate, Link } from 'react-router-dom'
import { WetacaLogo, InputText, InputCheck, Button } from '../components'
import { trpc } from '../trpc'
import { useSession } from '../auth/useSession'
import { mapAuthError } from '../lib/authErrors'
import { FORGOT_PASSWORD_PATH, HOME_PATH } from '../lib/routes'
import styles from './LoginPage.module.scss'

export const LoginPage = (): React.ReactElement => {
  const navigate = useNavigate()
  const { isAuthenticated, isLoading } = useSession()
  const queryClient = useQueryClient()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [rememberMe, setRememberMe] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  const loginMutation = trpc.auth.login.useMutation({
    onSuccess: () => {
      // Purge before entering: logout already clears the cache, but a session
      // that ended without one (tab closed, crash) can leave the previous user's
      // data in memory. Clearing also drops the stale `auth.me`, so the mounted
      // observers refetch it as this user.
      queryClient.clear()
      navigate(HOME_PATH, { replace: true })
    },
    onError: (err) => {
      setErrorMsg(mapAuthError(err, 'login'))
    },
  })

  // Already authenticated — go home
  if (!isLoading && isAuthenticated) {
    return <Navigate to={HOME_PATH} replace />
  }

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    setErrorMsg(null)
    // One email = one retailer: the backend derives the tenant from the user,
    // so one build serves every chain (no VITE_CLIENT_ID).
    loginMutation.mutate({ email, password, rememberMe })
  }

  return (
    <div className={`row-flx flx-a-c flx-j-c p-24 bg-light-cream ${styles['login-page']}`}>
      <div className={`p-v-40 p-h-32 border-s bg-white full-width ${styles['login-page__card']}`}>
        <div className="row-flx flx-j-c m-b-24">
          <WetacaLogo width={148} height={28} />
        </div>

        <h1 className={`txt-a-c m-b-24 txt-kale ${styles['login-page__heading']}`}>Accede a tu cuenta</h1>

        <form onSubmit={handleSubmit} className="col-flx flx-gap-16" noValidate>
          <InputText
            label="Email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoComplete="email"
            required
          />

          <InputText
            label="Contraseña"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="current-password"
            required
          />

          <div className={`txt-kale ${styles['login-page__remember']}`}>
            <InputCheck
              label="Mantener sesión abierta"
              checked={rememberMe}
              onChange={(event) => setRememberMe(event.target.checked)}
            />
          </div>

          {errorMsg != null && (
            <p role="alert" className={`m-0 txt-grana ${styles['login-page__error']}`}>
              {errorMsg}
            </p>
          )}

          <Button
            type="submit"
            variant="pill"
            label={loginMutation.isPending ? 'Entrando…' : 'Entrar'}
            bgColor="olive"
            txtColor="white"
            disabled={loginMutation.isPending}
            className="m-t-8 full-width"
          />
        </form>

        <div className={`txt-a-c ${styles['login-page__forgot-wrap']}`}>
          <Link to={FORGOT_PASSWORD_PATH} className={`txt-olive txt-underline ${styles['login-page__forgot-link']}`}>
            ¿Olvidaste tu contraseña?
          </Link>
        </div>
      </div>
    </div>
  )
}
